import React, { useState, useEffect, useCallback } from 'react';
import { Collapse, Menu, MenuItem, Divider } from '@material-ui/core';
import { ChevronDown, ChevronRight, LogOut } from 'lucide-react';
import { HistoryService } from '../../app/historyService';
import { RoleConstant } from '../../app/roleConstant';
import { buildNav } from './navConfig';
import LogoMobcheck from './images/logo_mobcheck_white.png';
import IconUser from './images/icon-user-white.png';

// Paleta Universo MOB (shell-web): navy escuro + ciano.
const NAVY = '#071a2c';
const NAVY_HOVER = 'rgba(28,165,217,0.10)';
const NAVY_ACTIVE = '#0d3350';
const CYAN = '#1CA5D9';
const FG = '#e6edf3';
const FG_MUTED = '#8aa0b2';
const BORDER = 'rgba(255,255,255,0.08)';
const WIDTH = 264;

const isActive = (path, current) =>
    path === '/' ? current === '/' : (current === path || current.startsWith(path + '/'));

function NavRow({ icon: Icon, label, active, depth, onClick, trailing }) {
    return (
        <div
            role="button"
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                padding: '9px 16px', paddingLeft: 16 + depth * 16,
                color: active ? CYAN : FG,
                background: active ? NAVY_ACTIVE : 'transparent',
                borderLeft: active ? `3px solid ${CYAN}` : '3px solid transparent',
                fontWeight: active ? 600 : 500, fontSize: '0.85rem',
                transition: 'background 0.15s, color 0.15s', userSelect: 'none',
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = NAVY_HOVER; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
        >
            {Icon && <Icon size={18} style={{ flexShrink: 0 }} />}
            <span style={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            {trailing}
        </div>
    );
}

export default function AppSidebar({ loggedUser, currentLanguage }) {
    const [path, setPath] = useState(HistoryService.path());
    const [openGroups, setOpenGroups] = useState({});
    const [userAnchor, setUserAnchor] = useState(null);

    const nav = buildNav(loggedUser.currentRole, currentLanguage);

    // Re-render on external navigation (hash change).
    useEffect(() => {
        const onHash = () => setPath(HistoryService.path());
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    // Auto-abre o grupo que contém a rota ativa.
    useEffect(() => {
        setOpenGroups(prev => {
            const next = { ...prev };
            nav.forEach(entry => {
                if (entry.children && entry.children.some(c => isActive(c.path, path))) {
                    next[entry.key] = true;
                }
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, loggedUser.currentRole]);

    const navigate = useCallback(p => {
        HistoryService.goto(p);
        setPath(HistoryService.path());
    }, []);

    const toggleGroup = key => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

    const closeUserMenu = () => setUserAnchor(null);
    const gotoFromUser = p => { closeUserMenu(); navigate(p); };

    const canMyPlan = [RoleConstant.COMPANY_GROUP_ADMIN, RoleConstant.MOBCHECK_ADMIN, RoleConstant.SERVICE_ADMIN]
        .includes(loggedUser.currentRole);

    return (
        <nav
            style={{
                width: WIDTH, minWidth: WIDTH, background: NAVY, color: FG,
                position: 'sticky', top: 0, alignSelf: 'flex-start',
                height: '100vh', display: 'flex', flexDirection: 'column',
                borderRight: `1px solid ${BORDER}`,
            }}
        >
            {/* Logo */}
            <div
                onClick={() => navigate('/')}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: 64, borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', flexShrink: 0,
                }}
            >
                <img alt="logo" src={LogoMobcheck} style={{ height: '2.2rem' }} />
            </div>

            {/* Navegação */}
            <div style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
                {nav.map((entry, i) => {
                    if (!entry.children) {
                        return (
                            <NavRow
                                key={entry.path || i}
                                icon={entry.icon}
                                label={entry.label}
                                depth={0}
                                active={isActive(entry.path, path)}
                                onClick={() => navigate(entry.path)}
                            />
                        );
                    }
                    const open = !!openGroups[entry.key];
                    const groupActive = entry.children.some(c => isActive(c.path, path));
                    return (
                        <div key={entry.key}>
                            <NavRow
                                icon={entry.icon}
                                label={entry.label}
                                depth={0}
                                active={groupActive && !open}
                                onClick={() => toggleGroup(entry.key)}
                                trailing={open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            />
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                {entry.children.map(child => (
                                    <NavRow
                                        key={child.path}
                                        icon={child.icon}
                                        label={child.label}
                                        depth={1}
                                        active={isActive(child.path, path)}
                                        onClick={() => navigate(child.path)}
                                    />
                                ))}
                            </Collapse>
                        </div>
                    );
                })}
            </div>

            {/* Usuário */}
            <div style={{ borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
                <div
                    role="button"
                    onClick={e => setUserAnchor(e.currentTarget)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = NAVY_HOVER)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                    <img
                        src={loggedUser.image ? 'data:image/png;base64,' + loggedUser.image : IconUser}
                        alt=""
                        style={{ height: 32, width: 32, borderRadius: '50%', objectFit: 'cover', background: NAVY_ACTIVE }}
                    />
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {loggedUser.name}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: FG_MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {loggedUser.companyName}
                        </div>
                    </div>
                    <LogOut size={16} color={FG_MUTED} />
                </div>
            </div>

            <Menu
                anchorEl={userAnchor}
                open={Boolean(userAnchor)}
                onClose={closeUserMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem onClick={() => gotoFromUser('/profile')}>{currentLanguage.profile}</MenuItem>
                <MenuItem onClick={() => gotoFromUser('/change-password')}>{currentLanguage.change_password}</MenuItem>
                <MenuItem onClick={() => gotoFromUser('/video-lessons')}>{currentLanguage.video_lessons}</MenuItem>
                {canMyPlan && <MenuItem onClick={() => gotoFromUser('/my-plan')}>{currentLanguage.my_plan}</MenuItem>}
                <Divider />
                <MenuItem onClick={() => gotoFromUser('/logout')}>{currentLanguage.logout}</MenuItem>
            </Menu>
        </nav>
    );
}
